// Kernel render primitives — the stable visual vocabulary that saved
// kernels render through.
//
// Each primitive lives in its own file and exports `{ kind, Component }`.
// The registry below maps `spec.kind` → component for the dispatcher.
// To add a primitive:
//   1. Add a doc string to src/lib/kernelPrimitiveSpecs.js and append
//      its kind to PRIMITIVE_KINDS — that registers it with the LLM
//      tool and the validators.
//   2. Add a file under this directory exporting `kind` + `Component`.
//   3. Import + register it below.
//
// Styling commitments enforced across all primitives:
//   • Rectangular, no rounded edges (radius 2px only).
//   • Dashboard color tokens — no hard-coded hex outside fallbacks.
//   • No native scrollbars — primitives never overflow vertically;
//     density-control is in the spec (maxRows / maxItems).
//   • Linkable rows / cells route through onOpenScope so kernels work
//     the same in chat (deep-link into scope) and on the activity
//     surface (navigate within the dashboard).

import { KernelEmpty } from "./shared.jsx";

import * as Sparkline from "./sparkline.jsx";
import * as Chart from "./chart.jsx";
import * as Stat from "./stat.jsx";
import * as Bars from "./bars.jsx";
import * as Table from "./table.jsx";
import * as Rows from "./rows.jsx";
import * as Brief from "./brief.jsx";
import * as MetricRail from "./metricRail.jsx";
import * as Distribution from "./distribution.jsx";
import * as ShareBar from "./shareBar.jsx";
import * as Waterfall from "./waterfall.jsx";
import * as DeltaBars from "./deltaBars.jsx";
import * as RefBars from "./refBars.jsx";
import * as Slopegraph from "./slopegraph.jsx";

import * as Kpis from "./kpis.jsx";
import * as Donut from "./donut.jsx";
import * as Heatmap from "./heatmap.jsx";
import * as Calendar from "./calendar.jsx";
import * as Scatter from "./scatter.jsx";
import * as Funnel from "./funnel.jsx";
import * as Boxplot from "./boxplot.jsx";
import * as Treemap from "./treemap.jsx";
import * as Composition from "./composition.jsx";
import * as Timeline from "./timeline.jsx";
import * as Radar from "./radar.jsx";
import * as Parallel from "./parallel.jsx";
import * as Sankey from "./sankey.jsx";
import * as Chord from "./chord.jsx";
import * as Sunburst from "./sunburst.jsx";
import * as MarkdownPrimitive from "./markdown.jsx";
import * as Relations from "./relations.jsx";

const modules = [
    Sparkline,
    Chart,
    Stat,
    Bars,
    Table,
    Rows,
    Brief,
    MetricRail,
    Distribution,
    ShareBar,
    Waterfall,
    DeltaBars,
    RefBars,
    Slopegraph,
    Kpis,
    Donut,
    Heatmap,
    Calendar,
    Scatter,
    Funnel,
    Boxplot,
    Treemap,
    Composition,
    Timeline,
    Radar,
    Parallel,
    Sankey,
    Chord,
    Sunburst,
    Relations,
    MarkdownPrimitive,
];

export const PRIMITIVES = Object.fromEntries(
    modules.map((m) => [m.kind, { Component: m.Component }]),
);

// Top-level dispatcher. Unknown kinds render a small inline error
// rather than crashing — kernels can ship with newer kinds than the
// running primitive set, and we'd rather degrade than blow up.
//
// `fillHeight`: when true, primitives that have a natural height
// (chart, sparkline) stretch to fill their parent instead of using
// the spec's pixel height. Used by the activity-surface widget so
// kernel charts read at the same scale as the regular lens charts.
// Looks-like-a-step-ref heuristic. The agent occasionally passes a
// string like "result" or "output" expecting it to resolve to the
// prior tool's output — there's no such resolution in render_block,
// so the chart silently renders empty. Catch the common shapes and
// surface a clear error instead.
const REF_NAME_RE = /^[a-z][a-z0-9_]{0,30}$/i;
function looksLikeStepRef(data, kind) {
    if (typeof data !== "string") return false;
    if (!data) return false;
    // `stat` legitimately accepts a string for display purposes, so
    // don't flag it there. Every other kind expects array/object/number.
    if (kind === "stat") return false;
    return REF_NAME_RE.test(data);
}

export function KernelBlock({ spec, data, onOpenScope, onFocus, linkifyText, fillHeight = false }) {
    if (!spec || typeof spec !== "object") {
        return <KernelEmpty message="invalid render spec" />;
    }
    const entry = PRIMITIVES[spec.kind];
    if (!entry) return <KernelEmpty message={`unknown kernel kind: ${spec.kind}`} />;
    if (looksLikeStepRef(data, spec.kind)) {
        return (
            <KernelEmpty
                message={`render_block received data: ${JSON.stringify(data)}. Pass inline JSON values instead of a step name; this tool does not resolve references.`}
            />
        );
    }
    const { Component } = entry;
    return (
        <Component
            spec={spec}
            data={data}
            onOpenScope={onOpenScope}
            onFocus={onFocus}
            linkifyText={linkifyText}
            fillHeight={fillHeight}
        />
    );
}

export { KernelEmpty };
